import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { 
  Edit3, 
  Save, 
  X,
  AlertCircle,
  Info
} from "lucide-react";
import { toast } from "sonner";

const EntityEditModal = ({ entity, open, onOpenChange, onSave, sources, targets, entities }) => {
  const [editedEntity, setEditedEntity] = useState({});
  const [isChanged, setIsChanged] = useState(false);

  // Update editedEntity when entity prop changes
  React.useEffect(() => {
    if (entity) {
      setEditedEntity(entity);
      setIsChanged(false);
    }
  }, [entity]);

  if (!entity) return null;

  const getEntityName = (entity) => {
    if (entity.sourceField && entity.sourceField.length > 0) {
      return entity.sourceField[0].entity || 'N/A';
    }
    return entity.entity || 'N/A';
  };

  const getSourceOptions = () => {
    if (!sources) return [];
    return sources.map(source => ({
      value: source.id,
      label: `${source.provider || source.source} (ID: ${source.id})`
    }));
  };

  const getTargetOptions = () => {
    if (!targets) return [];
    return targets.map(target => ({
      value: target.id,
      label: `${target.name} (ID: ${target.id})`
    }));
  };

  // Group targets by source-target pairs with entity information
  const getGroupedTargets = () => {
    if (!entities || !targets) return [];
    
    try {
      const grouped = {};
      
      entities.forEach(entity => {
        const key = `${entity.source} → ${entity.target}`;
        if (!grouped[key]) {
          grouped[key] = {
            sourceTarget: key,
            source: entity.source,
            target: entity.target,
            entities: []
          };
        }
        grouped[key].entities.push(entity);
      });

      return Object.values(grouped);
    } catch (error) {
      console.error('Error grouping targets:', error);
      return [];
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedEntity(prev => ({
      ...prev,
      [field]: value
    }));
    setIsChanged(true);
  };

  const handleSave = () => {
    if (onSave) {
      onSave(editedEntity);
    }
    toast.success("Entity updated successfully!");
    setIsChanged(false);
    onOpenChange(false);
  };

  const handleCancel = () => {
    if (isChanged) {
      const confirmed = window.confirm("You have unsaved changes. Are you sure you want to cancel?");
      if (!confirmed) return;
    }
    setEditedEntity(entity);
    setIsChanged(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-white" style={{backgroundColor: 'white', color: '#0f172a'}}>
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2 text-slate-900">
            <Edit3 className="w-5 h-5" />
            <span>Edit Entity: {editedEntity.source} → {editedEntity.target}</span>
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Entity: {getEntityName(editedEntity)} | ID: {editedEntity.id}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="basic" className="w-full" style={{color: '#0f172a'}}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="configuration">Configuration</TabsTrigger>
            <TabsTrigger value="advanced">Advanced</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="source" style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Source System</Label>
                <Input
                  id="source"
                  value={editedEntity.source || ''}
                  onChange={(e) => handleFieldChange('source', e.target.value)}
                  placeholder="e.g., servicenow"
                  className="bg-white border-slate-300 text-slate-900"
                  style={{backgroundColor: 'white', color: '#0f172a'}}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="target" style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Target System</Label>
                <Input
                  id="target"
                  value={editedEntity.target || ''}
                  onChange={(e) => handleFieldChange('target', e.target.value)}
                  placeholder="e.g., freshservice"
                  className="bg-white border-slate-300 text-slate-900"
                  style={{backgroundColor: 'white', color: '#0f172a'}}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="migrationObject" style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Migration Object ID</Label>
                <Select 
                  value={editedEntity.migrationObject?.toString() || ''} 
                  onValueChange={(value) => handleFieldChange('migrationObject', parseInt(value))}
                >
                  <SelectTrigger style={{backgroundColor: 'white', color: '#0f172a'}}>
                    <SelectValue placeholder="Select migration source..." />
                  </SelectTrigger>
                  <SelectContent style={{backgroundColor: 'white', color: '#0f172a'}}>
                    {getSourceOptions().map(option => (
                      <SelectItem key={option.value} value={option.value.toString()}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="mainTarget" style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Main Target ID</Label>
                <Select 
                  value={editedEntity.mainTarget?.toString() || ''} 
                  onValueChange={(value) => handleFieldChange('mainTarget', parseInt(value))}
                >
                  <SelectTrigger style={{backgroundColor: 'white', color: '#0f172a'}}>
                    <SelectValue placeholder="Select main target..." />
                  </SelectTrigger>
                  <SelectContent style={{backgroundColor: 'white', color: '#0f172a'}}>
                    {getTargetOptions().map(option => (
                      <SelectItem key={option.value} value={option.value.toString()}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="customFieldKey" style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Custom Field Key</Label>
                <Input
                  id="customFieldKey"
                  value={editedEntity.customFieldKey || ''}
                  onChange={(e) => handleFieldChange('customFieldKey', e.target.value)}
                  placeholder="e.g., custom_fields"
                  className="bg-white border-slate-300 text-slate-900"
                  style={{backgroundColor: 'white', color: '#0f172a'}}
                />
              </div>
            </div>

            {/* Incompatible Items */}
            <div className="space-y-2">
              <Label htmlFor="incompatible" style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Incompatible Items (comma-separated)</Label>
              <Textarea
                id="incompatible"
                value={editedEntity.incompatible ? editedEntity.incompatible.join(', ') : ''}
                onChange={(e) => handleFieldChange('incompatible', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                placeholder="Service Request, Changes, Problems, Articles"
                rows={3}
                className="bg-white border-slate-300 text-slate-900"
                style={{backgroundColor: 'white', color: '#0f172a'}}
              />
            </div>
          </TabsContent>

          <TabsContent value="configuration" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm text-slate-900 font-semibold">Delimiter Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="isDelimiterEnabled"
                      checked={editedEntity.isDelimiterEnabled || false}
                      onCheckedChange={(checked) => handleFieldChange('isDelimiterEnabled', checked)}
                    />
                    <Label htmlFor="isDelimiterEnabled" style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Enable Delimiter</Label>
                  </div>

                  {editedEntity.isDelimiterEnabled && (
                    <div className="space-y-2">
                      <Label htmlFor="delimiter" style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Delimiter Character</Label>
                      <Input
                        id="delimiter"
                        value={editedEntity.delimiter || ''}
                        onChange={(e) => handleFieldChange('delimiter', e.target.value)}
                        placeholder="|"
                        maxLength={5}
                        className="bg-white border-slate-300 text-slate-900"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm text-slate-900 font-semibold">Entity Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="selectMultipleEntities"
                      checked={editedEntity.selectMultipleEntities || false}
                      onCheckedChange={(checked) => handleFieldChange('selectMultipleEntities', checked)}
                    />
                    <Label htmlFor="selectMultipleEntities" style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Allow Multiple Entities</Label>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Dependent Targets */}
            <div className="space-y-2">
              <Label htmlFor="dependentTargets" style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Dependent Targets</Label>
              <div className="space-y-4">
                {getGroupedTargets().length > 0 ? (
                  getGroupedTargets().map((group, groupIndex) => {
                    // Get all available targets for this source-target pair
                    const availableTargets = targets?.filter(target => {
                      // Show targets that are related to this group's entities
                      return group.entities.some(entityItem => 
                        entityItem.mainTarget === target.id ||
                        (entityItem.dependentTargets && entityItem.dependentTargets.includes(target.id)) ||
                        (entityItem.mappingTargets && entityItem.mappingTargets.includes(target.id))
                      );
                    }) || [];

                    if (availableTargets.length === 0) return null;

                    return (
                      <div key={groupIndex} className="border rounded-lg p-3 bg-slate-50">
                        <div className="font-medium text-sm mb-2 text-slate-700 flex items-center">
                          <span className="text-blue-600 mr-2">{group.sourceTarget}</span>
                          <span className="text-xs text-gray-500">({availableTargets.length} targets available)</span>
                        </div>
                        <div className="space-y-2">
                          {availableTargets.map(target => {
                            // Find which entity this target belongs to for display
                            const relatedEntity = group.entities.find(entityItem =>
                              entityItem.mainTarget === target.id ||
                              (entityItem.dependentTargets && entityItem.dependentTargets.includes(target.id)) ||
                              (entityItem.mappingTargets && entityItem.mappingTargets.includes(target.id))
                            );
                            
                            return (
                              <div key={`dep-${target.id}`} className="flex items-center space-x-2 ml-4">
                                <input
                                  type="checkbox"
                                  id={`dep-target-${target.id}`}
                                  checked={editedEntity.dependentTargets?.includes(target.id) || false}
                                  onChange={(e) => {
                                    const currentTargets = editedEntity.dependentTargets || [];
                                    if (e.target.checked) {
                                      handleFieldChange('dependentTargets', [...currentTargets, target.id]);
                                    } else {
                                      handleFieldChange('dependentTargets', currentTargets.filter(id => id !== target.id));
                                    }
                                  }}
                                  className="rounded border-gray-300"
                                />
                                <Label htmlFor={`dep-target-${target.id}`} style={{color: '#0f172a', fontSize: '14px'}}>
                                  <span className="font-medium">
                                    {relatedEntity ? getEntityName(relatedEntity) : 'Unknown Entity'}
                                  </span>
                                  <span className="text-gray-500 ml-2">({target.name} - ID: {target.id})</span>
                                </Label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  // Fallback to original flat list if grouping fails
                  <div className="space-y-2">
                    {getTargetOptions().map(option => (
                      <div key={option.value} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`dep-${option.value}`}
                          checked={editedEntity.dependentTargets?.includes(option.value) || false}
                          onChange={(e) => {
                            const currentTargets = editedEntity.dependentTargets || [];
                            if (e.target.checked) {
                              handleFieldChange('dependentTargets', [...currentTargets, option.value]);
                            } else {
                              handleFieldChange('dependentTargets', currentTargets.filter(id => id !== option.value));
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                        <Label htmlFor={`dep-${option.value}`} style={{color: '#0f172a', fontSize: '14px'}}>
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Mapping Targets */}
            <div className="space-y-2">
              <Label htmlFor="mappingTargets" style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Mapping Targets</Label>
              <div className="space-y-4">
                {getGroupedTargets().length > 0 ? (
                  getGroupedTargets().map((group, groupIndex) => {
                    // Get all available targets for this source-target pair
                    const availableTargets = targets?.filter(target => {
                      // Show targets that are related to this group's entities
                      return group.entities.some(entityItem => 
                        entityItem.mainTarget === target.id ||
                        (entityItem.dependentTargets && entityItem.dependentTargets.includes(target.id)) ||
                        (entityItem.mappingTargets && entityItem.mappingTargets.includes(target.id))
                      );
                    }) || [];

                    if (availableTargets.length === 0) return null;

                    return (
                      <div key={`map-${groupIndex}`} className="border rounded-lg p-3 bg-slate-50">
                        <div className="font-medium text-sm mb-2 text-slate-700 flex items-center">
                          <span className="text-green-600 mr-2">{group.sourceTarget}</span>
                          <span className="text-xs text-gray-500">({availableTargets.length} targets available)</span>
                        </div>
                        <div className="space-y-2">
                          {availableTargets.map(target => {
                            // Find which entity this target belongs to for display
                            const relatedEntity = group.entities.find(entityItem =>
                              entityItem.mainTarget === target.id ||
                              (entityItem.dependentTargets && entityItem.dependentTargets.includes(target.id)) ||
                              (entityItem.mappingTargets && entityItem.mappingTargets.includes(target.id))
                            );
                            
                            return (
                              <div key={`map-${target.id}`} className="flex items-center space-x-2 ml-4">
                                <input
                                  type="checkbox"
                                  id={`map-target-${target.id}`}
                                  checked={editedEntity.mappingTargets?.includes(target.id) || false}
                                  onChange={(e) => {
                                    const currentTargets = editedEntity.mappingTargets || [];
                                    if (e.target.checked) {
                                      handleFieldChange('mappingTargets', [...currentTargets, target.id]);
                                    } else {
                                      handleFieldChange('mappingTargets', currentTargets.filter(id => id !== target.id));
                                    }
                                  }}
                                  className="rounded border-gray-300"
                                />
                                <Label htmlFor={`map-target-${target.id}`} style={{color: '#0f172a', fontSize: '14px'}}>
                                  <span className="font-medium">
                                    {relatedEntity ? getEntityName(relatedEntity) : 'Unknown Entity'}
                                  </span>
                                  <span className="text-gray-500 ml-2">({target.name} - ID: {target.id})</span>
                                </Label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  // Fallback to original flat list if grouping fails
                  <div className="space-y-2">
                    {getTargetOptions().map(option => (
                      <div key={option.value} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`map-${option.value}`}
                          checked={editedEntity.mappingTargets?.includes(option.value) || false}
                          onChange={(e) => {
                            const currentTargets = editedEntity.mappingTargets || [];
                            if (e.target.checked) {
                              handleFieldChange('mappingTargets', [...currentTargets, option.value]);
                            } else {
                              handleFieldChange('mappingTargets', currentTargets.filter(id => id !== option.value));
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                        <Label htmlFor={`map-${option.value}`} style={{color: '#0f172a', fontSize: '14px'}}>
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="advanced" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm text-slate-900 font-semibold">JSON Configuration Editor</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {editedEntity.sourceField && (
                  <div>
                    <Label style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Source Field Configuration</Label>
                    <Textarea
                      value={JSON.stringify(editedEntity.sourceField, null, 2)}
                      onChange={(e) => {
                        try {
                          const parsed = JSON.parse(e.target.value);
                          handleFieldChange('sourceField', parsed);
                        } catch (error) {
                          // Invalid JSON, don't update
                        }
                      }}
                      className="font-mono text-xs min-h-32"
                      style={{backgroundColor: 'white', color: '#0f172a'}}
                      placeholder="Enter valid JSON..."
                    />
                  </div>
                )}

                {editedEntity.targetField && (
                  <div>
                    <Label style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Target Field Configuration</Label>
                    <Textarea
                      value={JSON.stringify(editedEntity.targetField, null, 2)}
                      onChange={(e) => {
                        try {
                          const parsed = JSON.parse(e.target.value);
                          handleFieldChange('targetField', parsed);
                        } catch (error) {
                          // Invalid JSON, don't update
                        }
                      }}
                      className="font-mono text-xs min-h-32"
                      style={{backgroundColor: 'white', color: '#0f172a'}}
                      placeholder="Enter valid JSON..."
                    />
                  </div>
                )}

                {editedEntity.sourceExtender && (
                  <div>
                    <Label style={{color: '#0f172a', fontWeight: '600', fontSize: '14px'}}>Source Extender Configuration</Label>
                    <Textarea
                      value={JSON.stringify(editedEntity.sourceExtender, null, 2)}
                      onChange={(e) => {
                        try {
                          const parsed = JSON.parse(e.target.value);
                          handleFieldChange('sourceExtender', parsed);
                        } catch (error) {
                          // Invalid JSON, don't update
                        }
                      }}
                      className="font-mono text-xs min-h-32"
                      style={{backgroundColor: 'white', color: '#0f172a'}}
                      placeholder="Enter valid JSON..."
                    />
                  </div>
                )}

                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-2">
                      <Info className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">JSON Editing</span>
                    </div>
                    <p className="text-sm text-blue-700 mt-2">
                      Edit JSON configurations directly. Invalid JSON will be ignored. 
                      Make sure to validate your JSON structure before saving.
                    </p>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Action Buttons */}
        <div className="flex justify-between pt-4 border-t">
          <div>
            {isChanged && (
              <span className="text-sm text-orange-600 flex items-center space-x-1">
                <AlertCircle className="w-3 h-3" />
                <span>You have unsaved changes</span>
              </span>
            )}
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={handleCancel}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!isChanged}>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EntityEditModal;